import React, { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import global from "../../scss/dhp.scss";

import { EditorContext } from "../../containers/editor/EditorLayout";
import { CHART, COUNTDOWN, GROUP_WIDGET, TYPE_INFOGRAPHIC, TYPE_PROPOSAL } from "../../constants/editor";
import {
  changeBucketColorAccordingBackground,
  getClassListToString,
  hexToRGB,
  renderChartFromConfig,
  processWidgetHTML,
} from "../../_helpers/utils";
import useReplaceDuplicateShapeId from "../../hooks/useReplaceDuplicateShapeId";
import useCountdown from "../../hooks/useCountdown";
import { widgetConfig } from "./editor_config";

let style = Object.assign({}, global);

const CountdownRender = ({ config }) => {
  const innerRef = useRef(null);
  const { d, h, m, s } = useCountdown(config);
  const scale = config.scale || 1;
  const textColor =
    changeBucketColorAccordingBackground(hexToRGB(config.backgroundColor)) === "dark" ? "#ffffff" : "#000000";
  const blocks =
    d > 0
      ? [
          { label: "DAYS", value: d },
          { label: "HOURS", value: h },
          { label: "MINUTES", value: m },
        ]
      : [
          { label: "HOURS", value: h },
          { label: "MINUTES", value: m },
          { label: "SECONDS", value: s },
        ];

  useEffect(() => {
    if (!innerRef.current) return;

    const style = config.countdownStyle || {};

    // FORCE APPLY
    if (style.opacity !== undefined) {
      innerRef.current.style.opacity = style.opacity;
    }

    if (style.filter) {
      innerRef.current.style.filter = style.filter;
    }
  }, [config.countdownStyle]);

  return (
    <div
      key={JSON.stringify(config.countdownStyle)}
      ref={innerRef}
      className="dhp-widget-inner"
      style={{
        ...widgetConfig[COUNTDOWN].innerHTML.style,

        // APPLY USER STYLES
        ...(config.countdownStyle || {}),

        // fallback (used before resize)
        "--countdown-font": `${24 * scale}px`,
        "--countdown-label": `${10 * scale}px`,
        "--countdown-gap": `${6 * scale}px`,
      }}>
      {/* ================= STYLE 1 ================= */}
      {config.style === "style 1" && (
        <div
          style={{
            display: "flex",
            gap: "var(--countdown-gap)",
            width: "100%",
            height: "100%",
          }}>
          {blocks.map((b, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: config.backgroundColor,
                color: textColor,
                borderRadius: 6,
                fontFamily: config.fontFamily,
              }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "var(--countdown-font)",
                }}>
                {String(b.value).padStart(2, "0")}
              </div>

              <div
                style={{
                  fontSize: "var(--countdown-label)",
                }}>
                {b.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= STYLE 2 ================= */}
      {config.style === "style 2" && (
        <div
          style={{
            display: "flex",
            gap: "var(--countdown-gap)",
            width: "100%",
            height: "100%",
          }}>
          {blocks.map((b, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: config.backgroundColor,
                color: textColor,
                borderRadius: 6,
                fontFamily: config.fontFamily,
              }}>
              <div style={{ fontSize: "var(--countdown-label)" }}>{b.label}</div>

              <div
                style={{
                  width: "60%",
                  height: 1,
                  background: "currentColor",
                  opacity: 0.4,
                  margin: "4px 0",
                }}
              />

              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "var(--countdown-font)",
                }}>
                {String(b.value).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Block = props => {
  let { blockNodes, backgroundColors, backgroundImages, widgets, dimension, documentType } = props?.docPagesMeta
    ? { ...props.docPagesMeta }
    : useContext(EditorContext);

  const isDocumentAnalyticsEnabled = ["preview", "present"].includes(props?.source);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariBugfixCss = isSafari ? { willChange: "filter" } : {};
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();

  // Replace duplicate id after widget modification
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, [widgets]);

  // Replace duplicate id for shape use in shape border
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, []);

  return (
    <div
      style={
        [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
          ? { transformOrigin: "0px 0px", transform: `scale(${props.scaleFactor})` }
          : { height: `${(props.figureWidth / dimension.width) * dimension.height}px` }
      }>
      {blockNodes.map(
        blockNode =>
          blockNode.pageId === props.pageNode.pageId && (
            <div
              key={blockNode.blockId}
              {...(isDocumentAnalyticsEnabled && {
                id: `section-${blockNode.blockId}`,
              })}
              className="section"
              style={
                [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
                  ? blockNode.style
                  : props.origin === "notification"
                    ? {
                        ...blockNode.style,
                        transformOrigin: `${(props.figureWidth - dimension.width * props.scaleFactor) / 2}px 0px`,
                        transform: `scale(${props.scaleFactor})`,
                      }
                    : { ...blockNode.style, transformOrigin: "0px 0px", transform: `scale(${props.scaleFactor})` }
              }>
              {backgroundColors.map(
                (backgroundColor, i) =>
                  backgroundColor.blockId === blockNode.blockId && <div key={i} style={backgroundColor.style}></div>
              )}

              {backgroundImages.map(
                (backgroundImage, i) =>
                  backgroundImage.blockId === blockNode.blockId && <div key={i} style={backgroundImage.style}></div>
              )}

              {widgets.map(
                widget =>
                  widget.blockId === blockNode.blockId && (
                    <>
                      {widget.data["data-asset-type"] !== COUNTDOWN && (
                        <div
                          key={widget.id}
                          {...(widget.data["data-asset-type"] !== GROUP_WIDGET && { "data-id": widget.id })}
                          {...{ "data-class": "dhp-root-widgets" }}
                          {...(props?.isPreviewPresentMode && widget.data)}
                          {...(props?.isPreviewPresentMode && { className: getClassListToString(widget.classLists) })}
                          {...(!props?.isPreviewPresentMode && { className: "dhp-widgets" })}
                          {...(!props?.isPreviewPresentMode &&
                            widget.classLists.includes("dhp-widget-hyperlinked") && {
                              className: "dhp-widgets dhp-widget-hyperlinked",
                            })}
                          {...(!props?.isPreviewPresentMode && widget.data)}
                          style={{
                            ...widget.style,
                            ...safariBugfixCss,
                            display: props?.origin === "play" ? "none" : "",
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              widget.data["data-asset-type"] === CHART &&
                              !["preview", "present"].includes(props?.source)
                                ? `<img style="width: 100%" src=${widget.chartThumb} />`
                                : processWidgetHTML(widget.innerHTML, widget),
                          }}
                          {...(props.isPresent ? { id: widget.id } : {})}
                          {...(props?.source === "preview" &&
                            widget.data["data-asset-type"] === GROUP_WIDGET && { "data-id": widget.id })}></div>
                      )}

                      {widget.data["data-asset-type"] === COUNTDOWN && (
                        <div
                          key={widget.id}
                          {...(widget.data["data-asset-type"] !== GROUP_WIDGET && { "data-id": widget.id })}
                          {...{ "data-class": "dhp-root-widgets" }}
                          {...(props?.isPreviewPresentMode && widget.data)}
                          {...(props?.isPreviewPresentMode && { className: getClassListToString(widget.classLists) })}
                          {...(!props?.isPreviewPresentMode && { className: "dhp-widgets" })}
                          {...(!props?.isPreviewPresentMode &&
                            widget.classLists.includes("dhp-widget-hyperlinked") && {
                              className: "dhp-widgets dhp-widget-hyperlinked",
                            })}
                          {...(!props?.isPreviewPresentMode && widget.data)}
                          style={{
                            ...widget.style,
                            ...safariBugfixCss,
                            display: props?.origin === "play" ? "none" : "",
                          }}
                          {...(props.isPresent ? { id: widget.id } : {})}
                          {...(props?.source === "preview" &&
                            widget.data["data-asset-type"] === GROUP_WIDGET && { "data-id": widget.id })}>
                          <CountdownRender config={widget.config} />
                        </div>
                      )}
                    </>
                  )
              )}
            </div>
          )
      )}
    </div>
  );
};
Block.propTypes = {
  scaleFactor: PropTypes.number.isRequired,
  figureWidth: PropTypes.number.isRequired,
  pageNode: PropTypes.object,
  docPagesMeta: PropTypes.object,
  isPreviewPresentMode: PropTypes.bool,
  source: PropTypes.string,
};

const SingleBlock = props => {
  let { backgroundColors, backgroundImages, widgets, dimension } = props?.docPagesMeta
    ? { ...props.docPagesMeta }
    : useContext(EditorContext);

  const isDocumentAnalyticsEnabled = ["preview", "present"].includes(props?.source);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariBugfixCss = isSafari ? { willChange: "filter" } : {};
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();

  // Replace duplicate id after widget modification
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, [widgets]);

  // Replace duplicate id for shape use in shape border
  useEffect(() => {
    replaceDuplicateIdFromSVG();
    repalceDuplicateIdFromFrame();
  }, []);

  return (
    <div style={{ height: `${(props.figureWidth / dimension.width) * parseFloat(props.blockNode.style.height)}px` }}>
      <div
        key={props.blockNode.blockId}
        {...(isDocumentAnalyticsEnabled && {
          id: `section-${props.blockNode.blockId}`,
          className: "section",
        })}
        style={{ ...props.blockNode.style, transformOrigin: "0px 0px", transform: `scale(${props.scaleFactor})` }}>
        {backgroundColors.map(
          (backgroundColor, i) =>
            backgroundColor.blockId === props.blockNode.blockId && <div key={i} style={backgroundColor.style}></div>
        )}

        {backgroundImages.map(
          (backgroundImage, i) =>
            backgroundImage.blockId === props.blockNode.blockId && <div key={i} style={backgroundImage.style}></div>
        )}

        {widgets.map(
          widget =>
            widget.blockId === props.blockNode.blockId && (
              <>
                {widget.data["data-asset-type"] !== COUNTDOWN && (
                  <div
                    key={widget.id}
                    {...(widget.data["data-asset-type"] !== GROUP_WIDGET && { "data-id": widget.id })}
                    {...(props?.isPreviewPresentMode && widget.data)}
                    {...(props?.isPreviewPresentMode && { className: getClassListToString(widget.classLists) })}
                    {...(!props?.isPreviewPresentMode && { className: "dhp-widgets" })}
                    {...(!props?.isPreviewPresentMode &&
                      widget.classLists.includes("dhp-widget-hyperlinked") && {
                        className: "dhp-widgets dhp-widget-hyperlinked",
                      })}
                    {...(!props?.isPreviewPresentMode && widget.data)}
                    style={{ ...widget.style, ...safariBugfixCss }}
                    dangerouslySetInnerHTML={{
                      __html:
                        widget.data["data-asset-type"] === CHART && !["preview", "present"].includes(props?.source)
                          ? `<img style="width: 100%" src=${widget.chartThumb} />`
                          : processWidgetHTML(widget.innerHTML, widget),
                    }}
                    {...(props.isPresent ? { id: widget.id } : {})}
                    {...(props?.source === "preview" &&
                      widget.data["data-asset-type"] === GROUP_WIDGET && { "data-id": widget.id })}></div>
                )}

                {widget.data["data-asset-type"] === COUNTDOWN && (
                  <div
                    key={widget.id}
                    {...(widget.data["data-asset-type"] !== GROUP_WIDGET && { "data-id": widget.id })}
                    {...{ "data-class": "dhp-root-widgets" }}
                    {...(props?.isPreviewPresentMode && widget.data)}
                    {...(props?.isPreviewPresentMode && { className: getClassListToString(widget.classLists) })}
                    {...(!props?.isPreviewPresentMode && { className: "dhp-widgets" })}
                    {...(!props?.isPreviewPresentMode &&
                      widget.classLists.includes("dhp-widget-hyperlinked") && {
                        className: "dhp-widgets dhp-widget-hyperlinked",
                      })}
                    {...(!props?.isPreviewPresentMode && widget.data)}
                    style={{ ...widget.style, ...safariBugfixCss }}
                    {...(props.isPresent ? { id: widget.id } : {})}
                    {...(props?.source === "preview" &&
                      widget.data["data-asset-type"] === GROUP_WIDGET && { "data-id": widget.id })}>
                    <CountdownRender config={widget.config} />
                  </div>
                )}
              </>
            )
        )}
      </div>
    </div>
  );
};
SingleBlock.propTypes = {
  scaleFactor: PropTypes.number.isRequired,
  figureWidth: PropTypes.number.isRequired,
  blockNode: PropTypes.object,
  docPagesMeta: PropTypes.object,
  isPreviewPresentMode: PropTypes.bool,
  source: PropTypes.string,
};

const CanvasPageHtmlGenaretor = props => {
  let { dimension, documentType, blockNodes, widgets } = props?.docPagesMeta?.dimension
    ? { ...props.docPagesMeta }
    : useContext(EditorContext);

  let scaleFactor;

  if (props?.origin === "notification" && [TYPE_PROPOSAL].includes(documentType)) {
    const blockWidth = parseFloat(blockNodes[0].style.width);
    const blockHeight = parseFloat(blockNodes[0].style.height);

    scaleFactor = Math.max(props.figureWidth / blockWidth, props.figureWidth / blockHeight);
  } else if (props?.origin === "notification") {
    scaleFactor = Math.max(props.figureWidth / dimension.width, props.figureWidth / dimension.height);
  } else {
    scaleFactor = props.figureWidth / dimension.width;
  }

  let figureHeight = props?.figureHeight
    ? props?.figureHeight
    : ([TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && ["aiTranslate"].includes(props.actionPont)) ||
        ([TYPE_PROPOSAL].includes(documentType) && ["download"].includes(props.actionPont))
      ? (props.figureWidth / parseFloat(blockNodes[props.pageNode.blockIdx].style.width)) *
        parseFloat(blockNodes[props.pageNode.blockIdx].style.height)
      : (props.figureWidth / dimension.width) * dimension.height;

  useEffect(() => {
    if (widgets && props?.chartRenderingRef) {
      const domRef = window.document.querySelector(props.chartRenderingRef);
      if (domRef) {
        renderChartFromConfig(domRef, "dhp-widgets", widgets);
      }
    }
  }, [props?.chartRenderingRef]);

  return (
    <React.Fragment key={props.blockIndex ? props.blockIndex : props.pageNode.pageIdx}>
      <div className={props.className} id={props?.id} style={props?.style}>
        <figure
          className={style["mb-0"]}
          style={{
            height: `${figureHeight}px`,
            width: `${props.figureWidth}px`,
            backgroundColor: ["play", "notification"].includes(props?.origin) ? "#ffffff" : "",
          }}>
          {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            (!props.actionPont || !["link", "aiTranslate", "download"].includes(props.actionPont)) && (
              <div>
                <Block
                  scaleFactor={scaleFactor}
                  pageNode={props.pageNode}
                  figureWidth={props.figureWidth}
                  docPagesMeta={props.docPagesMeta}
                  isPreviewPresentMode={props?.isPreviewPresentMode}
                  isPresent={props?.isPresent}
                  isPublishedPage={props?.isPublishedPage}
                  origin={props?.origin}
                  source={props?.source}
                />
              </div>
            )}

          {[TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            ["link", "aiTranslate", "download"].includes(props.actionPont) && (
              <div>
                <SingleBlock
                  scaleFactor={scaleFactor}
                  blockNode={props.pageNode}
                  figureWidth={props.figureWidth}
                  docPagesMeta={props.docPagesMeta}
                  isPreviewPresentMode={props?.isPreviewPresentMode}
                  isPresent={props?.isPresent}
                  isPublishedPage={props?.isPublishedPage}
                  source={props?.source}
                />
              </div>
            )}

          {![TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) && (
            <Block
              scaleFactor={scaleFactor}
              pageNode={props.pageNode}
              figureWidth={props.figureWidth}
              docPagesMeta={props.docPagesMeta}
              isPreviewPresentMode={props?.isPreviewPresentMode}
              isPresent={props?.isPresent}
              origin={props?.origin}
              source={props?.source}
            />
          )}
        </figure>

        {props.pageType && props.pageType !== "Block" && (
          <span>
            {props.pageType} {props.blockIndex !== undefined ? props.blockIndex + 1 : props.pageNode.pageIdx + 1}
          </span>
        )}

        {props.pageType && props.pageType === "Block" && (
          <span>
            {props.pageType} {props.blockIndex + 1}
            {props.pageNode.blockName ? `: ${props.pageNode.blockName}` : ""}
          </span>
        )}
      </div>
    </React.Fragment>
  );
};
CanvasPageHtmlGenaretor.propTypes = {
  className: PropTypes.string,
  figureWidth: PropTypes.number.isRequired,
  figureHeight: PropTypes.number,
  pageNode: PropTypes.object,
  pageType: PropTypes.string,
  docPagesMeta: PropTypes.object,
  id: PropTypes.string,
  style: PropTypes.object,
  isPreviewPresentMode: PropTypes.bool,
  actionPont: PropTypes.string,
  source: PropTypes.string,
};

export default CanvasPageHtmlGenaretor;
